import { hash } from '@ember/helper';
import type { TOC } from '@ember/component/template-only';
import type { WithBoundArgs } from '@glint/template';

const _ActionableRow: TOC<{
  Args: { striped: boolean };
  Element: HTMLDivElement;
  Blocks: { default: []; action?: [] };
}> = <template>
  <div
    class="py-3 flex flex-col md:flex-row flex-nowrap gap-4 {{if @striped 'even:tio-bg-sunken'}}"
    role="list"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

const _ActionableTerm: TOC<{ Element: HTMLElement; Blocks: { default: [] } }> = <template>
  <div class="shrink tio-text-sm-medium" role="term" ...attributes>
    {{yield}}
  </div>
</template>;

const _ActionableDetails: TOC<{ Element: HTMLDivElement; Blocks: { default: [] } }> = <template>
  <div
    class="grow grid items-center sm:grid-cols-1 lg:grid-flow-col lg:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

const _ActionableItem: TOC<{ Element: HTMLElement; Blocks: { default: [] } }> = <template>
  <div class="col-span-1 text-sm tio-fg-secondary sm:mt-0" role="listitem" ...attributes>
    {{yield}}
  </div>
</template>;

const _ActionButton: TOC<{ Element: HTMLDivElement; Blocks: { default: [] } }> = <template>
  <div class="tio-flex-loose shrink-0 text-right self-center" role="button" ...attributes>
    {{yield}}
  </div>
</template>;

interface ActionableListSignature {
  Args: {
    striped?: boolean;
  };
  Blocks: {
    default: [
      {
        Row: WithBoundArgs<typeof _ActionableRow, 'striped'>;
        Term: typeof _ActionableTerm;
        Item: typeof _ActionableItem;
        Action?: typeof _ActionButton;
        Details: typeof _ActionableDetails;
      },
    ];
  };
  Element: HTMLElement;
}

const ActionableList: TOC<ActionableListSignature> = <template>
  <div class="divide-y tio-divide-subtle" ...attributes>
    {{yield
      (hash
        Row=(component _ActionableRow striped=(if (eq @striped undefined) true @striped))
        Action=_ActionButton
        Details=_ActionableDetails
        Item=_ActionableItem
        Term=_ActionableTerm
      )
    }}
  </div>
</template>;

export default ActionableList;
